// Main App Layout for PlexiSystem 4.0 - ULEPSZONA ORGANIZACJA
import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { 
  Menu, Sun, Moon, Bell, LogOut, X, ChevronDown,
  Command, Star, Grid, User, Settings, Award,
  LayoutDashboard, Sparkles, Search, Filter
} from 'lucide-react';
import { useTheme } from '../context/ThemeContext';
import { useAuth, useRole, useModuleAccess } from '../context/AuthContext';
import { useOfferStats } from '../hooks/useOfferStats';
import { AnimatedBackground, backgroundAnimations } from '../components/ui/AnimatedBackground';
import { CommandPalette } from '../components/ui/CommandPalette';
import { MobileNavigation } from '../components/ui/MobileNavigation';
import { ModulesPanel } from '../components/ui/ModulesPanel';
import { menuCategories, allMenuItems, defaultFavorites } from '../config/menuConfig';

interface Notification {
  id: number;
  type: 'info' | 'success' | 'warning' | 'error';
  message: string;
  date: string;
  icon?: React.ElementType;
}

interface AppLayoutProps {
  children: React.ReactNode;
}

export const AppLayout: React.FC<AppLayoutProps> = ({ children }) => {
  const { theme, toggleTheme } = useTheme();
  const { user, role, logout } = useAuth();
  const { isAdmin, isKierownik, isHandlowiec, isOperator, isMagazynier, isKlient } = useRole();
  const { hasModuleAccess } = useModuleAccess();
  const { stats } = useOfferStats();
  const navigate = useNavigate();
  const location = useLocation();
  
  const [showNotifications, setShowNotifications] = useState(false);
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [showCommandPalette, setShowCommandPalette] = useState(false);
  const [showModulesPanel, setShowModulesPanel] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [dashboardType, setDashboardType] = useState<'standard' | 'crystal'>(() => {
    return localStorage.getItem('plexisystem_dashboard') as 'standard' | 'crystal' || 'standard';
  });
  const [favoriteModules, setFavoriteModules] = useState<string[]>(() => {
    const saved = localStorage.getItem('plexisystem_favorites');
    return saved ? JSON.parse(saved) : defaultFavorites;
  });
  
  const [notifications, setNotifications] = useState<Notification[]>([
    {
      id: 1,
      type: 'success',
      message: '🚀 PlexiSystem 4.0 - Ulepszona organizacja modułów!',
      date: new Date().toISOString()
    },
    {
      id: 2,
      type: 'info',
      message: '✨ Możesz teraz przełączać między dashboardami',
      date: new Date().toISOString()
    }
  ]);

  // Toggle dashboard type
  const toggleDashboard = () => {
    const newType = dashboardType === 'standard' ? 'crystal' : 'standard';
    setDashboardType(newType);
    localStorage.setItem('plexisystem_dashboard', newType);
    
    // Navigate to appropriate dashboard
    if (location.pathname === '/dashboard' || location.pathname === '/dashboard-new') {
      navigate(newType === 'crystal' ? '/dashboard-new' : '/dashboard');
    }
  };

  // Setup access check functions for menu items
  const setupMenuAccessChecks = () => {
    menuCategories.forEach(category => {
      category.items.forEach(item => {
        // Przypisz funkcje sprawdzania dostępu na podstawie ID
        switch(item.id) {
          case 'dashboard':
            item.checkAccess = () => hasModuleAccess('dashboard') || true;
            break;
          case 'calculator':
            item.checkAccess = () => hasModuleAccess('calculator') || !isKlient();
            break;
          case 'offers':
            item.checkAccess = () => hasModuleAccess('offers') || isAdmin() || isHandlowiec() || isKierownik();
            break;
          case 'clients':
            item.checkAccess = () => hasModuleAccess('clients') || isAdmin() || isHandlowiec() || isKierownik();
            break;
          case 'production':
            item.checkAccess = () => hasModuleAccess('production') || isAdmin() || isKierownik() || isOperator();
            break;
          case 'machining':
            item.checkAccess = () => hasModuleAccess('production') || isAdmin() || isKierownik() || isOperator();
            break;
          case 'quality':
            item.checkAccess = () => hasModuleAccess('quality_control') || isAdmin() || isKierownik() || isOperator();
            break;
          case 'profitability':
            item.checkAccess = () => hasModuleAccess('profitability') || isAdmin() || isKierownik() || isHandlowiec();
            break;
          case 'reports':
            item.checkAccess = () => hasModuleAccess('reports') || isAdmin() || isKierownik() || isHandlowiec();
            break;
          case 'payroll':
            item.checkAccess = () => hasModuleAccess('payroll') || isAdmin() || isKierownik();
            break;
          case 'social-media':
            item.checkAccess = () => hasModuleAccess('social_media') || isAdmin() || isHandlowiec() || isKierownik();
            break;
          case 'notifications':
            item.checkAccess = () => hasModuleAccess('notifications') || isAdmin() || isKierownik() || isHandlowiec();
            break;
          case 'ai-assistant':
            item.checkAccess = () => hasModuleAccess('ai_assistant') || isAdmin() || isHandlowiec();
            break;
          case 'automation':
            item.checkAccess = () => hasModuleAccess('automation') || isAdmin();
            break;
          case 'admin':
            item.checkAccess = () => hasModuleAccess('admin') || isAdmin();
            break;
          case 'system-health':
            item.checkAccess = () => isAdmin();
            break;
          default:
            item.checkAccess = () => true;
        }
      });
    });
  };

  // Initialize access checks
  useEffect(() => {
    setupMenuAccessChecks();
  }, [hasModuleAccess, isAdmin, isKierownik, isHandlowiec, isOperator, isKlient]);

  // Get favorite modules
  const favoriteMenuItems = allMenuItems.filter(item => 
    favoriteModules.includes(item.id) && (!item.checkAccess || item.checkAccess())
  );

  // Toggle favorite
  const toggleFavorite = (moduleId: string) => {
    setFavoriteModules(prev => {
      const newFavorites = prev.includes(moduleId)
        ? prev.filter(id => id !== moduleId)
        : [...prev, moduleId];
      localStorage.setItem('plexisystem_favorites', JSON.stringify(newFavorites));
      return newFavorites;
    });
  };

  // Handle logout
  const handleLogout = async () => {
    if (confirm('Czy na pewno chcesz się wylogować?')) {
      await logout();
    }
  };

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setShowCommandPalette(true);
      }
      if ((e.metaKey || e.ctrlKey) && e.key === 'm') {
        e.preventDefault();
        setShowModulesPanel(true);
      }
      if (e.key === 'Escape') {
        setShowCommandPalette(false);
        setShowModulesPanel(false);
        setShowNotifications(false);
        setShowMobileMenu(false);
        setShowUserMenu(false);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  // iOS optimizations
  useEffect(() => {
    document.body.style.overscrollBehavior = 'none';
    const metaViewport = document.querySelector('meta[name=viewport]');
    if (metaViewport) {
      metaViewport.setAttribute('content', 'width=device-width, initial-scale=1.0, viewport-fit=cover, user-scalable=no');
    }
    const isStandalone = window.matchMedia('(display-mode: standalone)').matches;
    if (isStandalone) {
      document.body.classList.add('ios-standalone');
    }
    return () => {
      document.body.style.overscrollBehavior = '';
    };
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-br from-zinc-950 via-black to-zinc-950 text-white transition-all duration-500">
      {/* Animated Background */}
      <AnimatedBackground />
      
      {/* iOS Status Bar Spacer */}
      <div className="h-[env(safe-area-inset-top)] bg-black/50 backdrop-blur-xl" />
      
      {/* Enhanced Header with Crystal Clear Design */}
      <header className="bg-black/30 backdrop-blur-2xl border-b border-white/10 sticky top-0 z-40">
        <div className="container mx-auto px-3 sm:px-6 py-3 sm:py-4">
          <div className="flex justify-between items-center gap-3">
            <div className="flex items-center gap-3 sm:gap-6">
              {/* Mobile Menu */}
              <button
                onClick={() => setShowMobileMenu(!showMobileMenu)}
                className="lg:hidden p-2.5 hover:bg-white/10 rounded-xl transition-all touch-manipulation"
              >
                {showMobileMenu ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
              </button>
              
              {/* Logo with enhanced gradient */}
              <h1 className="text-xl sm:text-2xl lg:text-3xl font-black bg-gradient-to-r from-violet-400 via-purple-400 to-pink-400 bg-clip-text text-transparent flex items-center gap-2 animate-float">
                <span className="text-2xl">✨</span>
                PlexiSystem
                <span className="text-xs font-normal text-gray-500 ml-2">v4.0</span>
              </h1>
              
              {/* Desktop Navigation - tylko ulubione (max 3) */}
              <nav className="hidden lg:flex items-center gap-2">
                {favoriteMenuItems.slice(0, 3).map(item => {
                  const Icon = item.icon;
                  const isActive = location.pathname === item.href;
                  
                  return (
                    <a
                      key={item.id}
                      href={item.href}
                      className={`group flex items-center gap-2 px-4 py-2.5 rounded-xl transition-all ${
                        isActive 
                          ? 'bg-gradient-to-r from-violet-500/20 to-purple-500/20 border border-violet-500/50' 
                          : 'hover:bg-white/10 border border-transparent'
                      }`}
                    >
                      <Icon className={`w-4 h-4 ${isActive ? 'text-violet-400' : 'text-white/70'}`} />
                      <span className={`text-sm font-medium ${isActive ? 'text-white' : 'text-white/70'}`}>
                        {item.label}
                      </span>
                    </a>
                  );
                })}
                
                {/* Modules Panel Button - ULEPSZONE */}
                <button
                  onClick={() => setShowModulesPanel(true)}
                  className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-gradient-to-r from-violet-500/20 to-purple-500/20 hover:from-violet-500/30 hover:to-purple-500/30 border border-violet-500/30 transition-all group"
                >
                  <Grid className="w-4 h-4 text-violet-400 group-hover:text-white" />
                  <span className="text-sm text-violet-300 group-hover:text-white font-medium">Wszystkie moduły</span>
                  <kbd className="px-2 py-0.5 text-xs bg-violet-500/20 text-violet-300 rounded-lg ml-2">⌘M</kbd>
                </button>
              </nav>
            </div>

            <div className="flex items-center gap-2 sm:gap-3">
              {/* Stats Pills */}
              <div className="hidden xl:flex items-center gap-2">
                <div className="px-3 py-1.5 bg-white/5 backdrop-blur rounded-full border border-white/10">
                  <span className="text-xs text-gray-400">Oferty: </span>
                  <span className="text-xs font-bold text-white">{stats.totalOffers}</span>
                </div>
                {stats.pendingOffers > 0 && (
                  <div className="px-3 py-1.5 bg-gradient-to-r from-yellow-500/20 to-orange-500/20 backdrop-blur rounded-full border border-yellow-500/50 animate-pulse">
                    <span className="text-xs text-yellow-400">Oczekujące: </span>
                    <span className="text-xs font-bold text-yellow-400">{stats.pendingOffers}</span>
                  </div>
                )}
              </div>

              {/* Dashboard Toggle - NOWE */}
              <button
                onClick={toggleDashboard}
                className="p-2.5 hover:bg-white/10 rounded-xl transition-all touch-manipulation group relative"
                title={`Przełącz na dashboard ${dashboardType === 'standard' ? 'Crystal' : 'Standard'}`}
              >
                {dashboardType === 'crystal' ? (
                  <Sparkles className="w-5 h-5 text-purple-400 group-hover:animate-spin" />
                ) : (
                  <LayoutDashboard className="w-5 h-5 text-blue-400 group-hover:animate-pulse" />
                )}
                <span className="absolute -bottom-8 left-1/2 transform -translate-x-1/2 px-2 py-1 bg-black/90 text-xs text-white rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap">
                  {dashboardType === 'crystal' ? 'Crystal' : 'Standard'}
                </span>
              </button>

              {/* Theme Toggle */}
              <button
                onClick={toggleTheme}
                className="p-2.5 hover:bg-white/10 rounded-xl transition-all touch-manipulation group"
                title={theme === 'dark' ? 'Jasny motyw' : 'Ciemny motyw'}
              >
                {theme === 'dark' ? (
                  <Sun className="w-5 h-5 text-yellow-400 group-hover:rotate-180 transition-transform duration-500" />
                ) : (
                  <Moon className="w-5 h-5 text-indigo-400 group-hover:-rotate-12 transition-transform duration-500" />
                )}
              </button>

              {/* Notifications */}
              <div className="relative">
                <button
                  onClick={() => setShowNotifications(!showNotifications)}
                  className="p-2.5 hover:bg-white/10 rounded-xl transition-all relative touch-manipulation group"
                >
                  <Bell className="w-5 h-5 group-hover:animate-wiggle" />
                  {notifications.length > 0 && (
                    <span className="absolute -top-1 -right-1 w-6 h-6 bg-gradient-to-r from-red-500 to-pink-500 rounded-full text-xs flex items-center justify-center animate-pulse">
                      {notifications.length}
                    </span>
                  )}
                </button>

                {/* Notifications Dropdown */}
                {showNotifications && (
                  <div className="absolute right-0 top-14 w-80 sm:w-96 bg-black/90 backdrop-blur-2xl rounded-2xl shadow-2xl border border-white/10 max-h-[70vh] overflow-hidden z-50">
                    <div className="sticky top-0 bg-black/50 backdrop-blur-xl p-4 border-b border-white/10 flex justify-between items-center">
                      <h3 className="font-bold bg-gradient-to-r from-white to-gray-400 bg-clip-text text-transparent">
                        Powiadomienia
                      </h3>
                      <button
                        onClick={() => setNotifications([])}
                        className="text-xs text-gray-400 hover:text-white transition-colors"
                      >
                        Wyczyść
                      </button>
                    </div>
                    <div className="overflow-y-auto max-h-[60vh] p-3">
                      {notifications.length === 0 ? (
                        <div className="py-12 text-center">
                          <Bell className="w-12 h-12 text-gray-600 mx-auto mb-3" />
                          <p className="text-gray-400">Brak nowych powiadomień</p>
                        </div>
                      ) : (
                        <div className="space-y-2">
                          {notifications.map(notification => (
                            <div
                              key={notification.id}
                              className={`p-4 rounded-xl border backdrop-blur-xl transition-all hover:scale-[1.02] ${
                                notification.type === 'success' ? 'bg-green-500/10 border-green-500/30' :
                                notification.type === 'error' ? 'bg-red-500/10 border-red-500/30' :
                                notification.type === 'warning' ? 'bg-yellow-500/10 border-yellow-500/30' :
                                'bg-blue-500/10 border-blue-500/30'
                              }`}
                            >
                              <p className="text-sm text-white">{notification.message}</p>
                              <p className="text-xs text-gray-400 mt-1">
                                {new Date(notification.date).toLocaleString('pl-PL')}
                              </p>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* User Menu */}
              <div className="relative">
                <button
                  onClick={() => setShowUserMenu(!showUserMenu)}
                  className="flex items-center gap-2 p-2 sm:px-3 sm:py-2 hover:bg-white/10 rounded-xl transition-all group"
                >
                  <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-violet-400 to-purple-400 flex items-center justify-center">
                    <span className="text-xs font-bold text-white">
                      {user?.full_name?.charAt(0) || user?.email?.charAt(0) || 'U'}
                    </span>
                  </div>
                  <div className="hidden sm:block text-left">
                    <p className="text-xs text-gray-400">{role?.display_name}</p>
                    <p className="text-sm font-medium">{user?.full_name || user?.email}</p>
                  </div>
                  <ChevronDown className="w-4 h-4 text-gray-400 group-hover:rotate-180 transition-transform" />
                </button>

                {/* User Dropdown */}
                {showUserMenu && (
                  <div className="absolute right-0 top-14 w-64 bg-black/90 backdrop-blur-2xl rounded-2xl shadow-2xl border border-white/10 overflow-hidden z-50">
                    <div className="p-4 border-b border-white/10">
                      <div className="flex items-center gap-3">
                        <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-violet-400 to-purple-400 flex items-center justify-center">
                          <span className="text-lg font-bold text-white">
                            {user?.full_name?.charAt(0) || user?.email?.charAt(0) || 'U'}
                          </span>
                        </div>
                        <div>
                          <p className="font-medium">{user?.full_name || 'Użytkownik'}</p>
                          <p className="text-xs text-gray-400">{user?.email}</p>
                        </div>
                      </div>
                    </div>
                    <div className="p-2">
                      <button className="w-full p-3 text-left hover:bg-white/10 rounded-xl transition-all flex items-center gap-3">
                        <User className="w-4 h-4 text-gray-400" />
                        <span className="text-sm">Mój profil</span>
                      </button>
                      <button className="w-full p-3 text-left hover:bg-white/10 rounded-xl transition-all flex items-center gap-3">
                        <Settings className="w-4 h-4 text-gray-400" />
                        <span className="text-sm">Ustawienia</span>
                      </button>
                      <button className="w-full p-3 text-left hover:bg-white/10 rounded-xl transition-all flex items-center gap-3">
                        <Award className="w-4 h-4 text-gray-400" />
                        <span className="text-sm">Osiągnięcia</span>
                      </button>
                      <div className="border-t border-white/10 mt-2 pt-2">
                        <button
                          onClick={handleLogout}
                          className="w-full p-3 text-left hover:bg-red-500/20 rounded-xl transition-all flex items-center gap-3 text-red-400"
                        >
                          <LogOut className="w-4 h-4" />
                          <span className="text-sm">Wyloguj się</span>
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Mobile Menu */}
      {showMobileMenu && (
        <MobileNavigation 
          categories={menuCategories} 
          onClose={() => setShowMobileMenu(false)}
        />
      )}

      {/* Command Palette - dla szybkiego wyszukiwania */}
      <CommandPalette 
        isOpen={showCommandPalette}
        onClose={() => setShowCommandPalette(false)}
        menuItems={allMenuItems}
      />

      {/* Modules Panel - NOWY KOMPONENT */}
      <ModulesPanel
        isOpen={showModulesPanel}
        onClose={() => setShowModulesPanel(false)}
        categories={menuCategories}
        favorites={favoriteModules}
        onToggleFavorite={toggleFavorite}
      />

      {/* Main Content */}
      <main className="container mx-auto px-3 sm:px-6 py-6 sm:py-8 lg:py-10 min-h-[calc(100vh-5rem)] pb-[calc(env(safe-area-inset-bottom)+2rem)]">
        <div className="relative z-10">
          {children}
        </div>
      </main>

      {/* iOS Home Indicator Safe Area */}
      <div className="h-[env(safe-area-inset-bottom)] bg-black/50" />
      
      {/* Add CSS animations */}
      <style>{backgroundAnimations}</style>
    </div>
  );
};